import { createContext, useContext, useEffect, useState, ReactNode, useCallback } from "react";
import { supabase } from "@/integrations/supabase/client";
import {
  DEFAULT_SETTINGS,
  SiteSettings,
  CarRow,
  mergeSettings,
} from "@/lib/site-content";

type Ctx = {
  settings: SiteSettings;
  cars: CarRow[];
  loading: boolean;
  reload: () => Promise<void>;
};

const SiteContentContext = createContext<Ctx | undefined>(undefined);

export function SiteContentProvider({ children }: { children: ReactNode }) {
  const [settings, setSettings] = useState<SiteSettings>(DEFAULT_SETTINGS);
  const [cars, setCars] = useState<CarRow[]>([]);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    setLoading(true);
    const [{ data: rows }, { data: carRows }] = await Promise.all([
      supabase.from("site_settings").select("data").limit(1),
      supabase
        .from("cars")
        .select("*")
        .eq("is_active", true)
        .order("category", { ascending: true })
        .order("sort_order", { ascending: true }),
    ]);

    const dbData = rows && rows[0] ? (rows[0].data as Partial<SiteSettings>) : null;
    setSettings(mergeSettings(DEFAULT_SETTINGS, dbData));
    setCars((carRows ?? []) as unknown as CarRow[]);
    setLoading(false);
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  // Live preview channel: when embedded in admin via ?preview=1, accept
  // postMessage updates and apply them without persisting to DB.
  useEffect(() => {
    if (typeof window === "undefined") return;
    const isPreview = new URLSearchParams(window.location.search).get("preview") === "1";
    if (!isPreview) return;
    const onMsg = (e: MessageEvent) => {
      const data = e.data as { type?: string; settings?: Partial<SiteSettings>; cars?: CarRow[] } | null;
      if (!data || typeof data !== "object") return;
      if (data.type === "lovable-preview-update") {
        if (data.settings) setSettings((cur) => mergeSettings(cur, data.settings!));
        if (Array.isArray(data.cars)) setCars(data.cars);
      }
    };
    window.addEventListener("message", onMsg);
    window.parent?.postMessage({ type: "lovable-preview-ready" }, "*");
    return () => window.removeEventListener("message", onMsg);
  }, []);

  // Apply theme variables + fonts
  useEffect(() => {
    const t = settings.theme;
    const root = document.documentElement;
    root.style.setProperty("--background", t.background);
    root.style.setProperty("--foreground", t.foreground);
    root.style.setProperty("--card", t.card);
    root.style.setProperty("--card-foreground", t.foreground);
    root.style.setProperty("--popover", t.card);
    root.style.setProperty("--popover-foreground", t.foreground);
    root.style.setProperty("--primary", t.primary);
    root.style.setProperty("--primary-foreground", t.primaryForeground);
    root.style.setProperty("--ring", t.primary);
    root.style.setProperty("--border", t.border);
    document.body.style.fontFamily = t.fontBody;
    root.style.setProperty("--font-heading", t.fontHeading);
    root.style.setProperty("--font-body", t.fontBody);
  }, [settings.theme]);

  return (
    <SiteContentContext.Provider value={{ settings, cars, loading, reload: load }}>
      {children}
    </SiteContentContext.Provider>
  );
}

export function useSiteContent() {
  const ctx = useContext(SiteContentContext);
  if (!ctx) throw new Error("useSiteContent must be used within SiteContentProvider");
  return ctx;
}
